import React, { Fragment, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import useScrollPositionThrottled from "../../hooks/useScrollPositionThrottled";
import { retrieveFeedPosts } from "../../services/feedServices";
import { normalizeMediaType } from "../../utils/media";

import MobileHeader from "../Header/MobileHeader/MobileHeader";
import ImageGrid from "../ImageGrid/ImageGrid";
import PreviewImage from "../PreviewImage/PreviewImage";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

const FollowingPosts = ({ token, showModal, showAlert }) => {
  const history = useHistory();
  const [state, setState] = useState({
    posts: [],
    fetching: false,
    hasMore: true,
    initialized: false,
  });

  const handlePostDialogDispatch = useCallback((action) => {
    if (!action || action.type !== "SET_POST_VOTES_COUNT") {
      return;
    }
    const { postId, postVotes, voteCount, reactionType, likeCount: nextLikeCount } =
      action.payload || {};
    if (!postId) {
      return;
    }
    setState((previous) => {
      const posts = previous.posts.map((post) => {
        const currentId =
          (post._id && post._id.toString()) ||
          (post.postId && post.postId.toString());
        if (currentId !== postId) {
          return post;
        }
        const hadReaction = post.reactionType === "like";
        const hasReaction = reactionType === "like";
        const incomingLikeCount =
          typeof nextLikeCount === "number" ? nextLikeCount : null;
        let likeCount =
          incomingLikeCount !== null ? incomingLikeCount : post.likeCount || 0;
        if (incomingLikeCount === null && typeof reactionType !== "undefined") {
          if (hasReaction && !hadReaction) {
            likeCount += 1;
          } else if (!hasReaction && hadReaction) {
            likeCount = Math.max(0, likeCount - 1);
          }
        }
        return {
          ...post,
          likeCount,
          postVotes: Array.isArray(postVotes) ? postVotes : post.postVotes,
          postVotesCount:
            typeof voteCount === "number" ? voteCount : post.postVotesCount,
          reactionType:
            typeof reactionType !== "undefined"
              ? reactionType
              : post.reactionType,
        };
      });
      return { ...previous, posts };
    });
  }, []);

  const openPost = (postId, avatar) => {
    if (window.outerWidth <= 600) {
      history.push(`/post/${postId}`);
    } else {
      showModal(
        {
          postId: postId.toString(),
          avatar,
          profileDispatch: handlePostDialogDispatch,
        },
        "PostDialog/PostDialog"
      );
    }
  };

  const loadPosts = useCallback(async (offset = 0) => {
    if (!token) {
      return;
    }

    try {
      setState((previous) => ({ ...previous, fetching: true }));
      const response = await retrieveFeedPosts(token, offset);

      setState((previous) => ({
        posts: offset > 0 ? [...previous.posts, ...response] : [...response],
        fetching: false,
        hasMore: response.length > 0,
        initialized: true,
      }));
    } catch (err) {
      const message = err?.message || "Unable to load posts.";
      showAlert(message, () => loadPosts(offset));
      setState((previous) => ({
        ...previous,
        fetching: false,
        initialized: true,
      }));
    }
  }, [token, showAlert]);

  useEffect(() => {
    loadPosts(0);
  }, [loadPosts]);

  useScrollPositionThrottled(
    ({ atBottom }) => {
      if (atBottom && state.hasMore && !state.fetching) {
        loadPosts(state.posts.length);
      }
    },
    null,
    [state, loadPosts]
  );

  const renderSkeleton = (amount) => {
    const skeleton = [];
    for (let i = 0; i < amount; i += 1) {
      skeleton.push(
        <SkeletonLoader
          key={`skeleton-${i}`}
          style={{ minHeight: "30rem" }}
          animated
        />
      );
    }
    return skeleton;
  };

  const parseMetadata = (metadata) => {
    if (!metadata) {
      return null;
    }
    if (typeof metadata === 'object') {
      return metadata;
    }
    try {
      return JSON.parse(metadata);
    } catch (err) {
      return null;
    }
  };

  const getMediaForPost = (post) => {
    const mediaItem = post.mediaItems && post.mediaItems[0];
    const directUrl = post.mediaUrl || post.image;
    const thumbnail =
      mediaItem?.thumbnailUrl || post.thumbnail || directUrl || null;
    const mediaUrl = mediaItem?.mediaUrl || directUrl || thumbnail;
    const metadata = parseMetadata(mediaItem?.metadata || post.metadata);
    const mediaType = normalizeMediaType(
      mediaItem?.mediaType || post.mediaType,
      mediaUrl
    );
    const filter = mediaItem?.filter || post.filter || '';
    return { mediaType, mediaUrl, thumbnail, filter, metadata };
  };

  const postsWithMedia = state.posts
    .map((post) => ({ ...post, _media: getMediaForPost(post) }))
    .filter((post) => post._media.mediaUrl);

  return (
    <Fragment>
      <MobileHeader>
        <div></div>
        <h3 className="heading-3">Following</h3>
        <div></div>
      </MobileHeader>
      <ImageGrid>
        {postsWithMedia.map((post) => {
          const { mediaType, mediaUrl, thumbnail, filter, metadata } =
            post._media;
          const aggregatedReactions =
            (post.likeCount || 0) +
            (post.loveCount || 0) +
            (post.hahaCount || 0);
          const likes =
            aggregatedReactions > 0
              ? aggregatedReactions
              : typeof post.postVotesCount === "number"
              ? post.postVotesCount
              : Array.isArray(post.postVotes)
              ? post.postVotes.length
              : Number(post.postVotes) || 0;
          const commentTotal =
            typeof post.commentCount === "number"
              ? post.commentCount
              : post.commentData?.comments?.length || 0;

          return (
            <PreviewImage
              key={post._id || post.postId}
              image={post.image}
              thumbnail={thumbnail}
              mediaUrl={mediaUrl}
              mediaType={mediaType}
              metadata={metadata}
              likes={likes}
              comments={commentTotal}
              commentCount={commentTotal}
              filter={filter}
              onClick={() =>
                openPost(
                  post.postId || post._id,
                  post.author?.avatar || post.avatar
                )
              }
            />
          );
        })}
        {state.fetching && renderSkeleton(6)}
      </ImageGrid>
      {!state.fetching && state.initialized && postsWithMedia.length === 0 && (
        <p style={{ textAlign: "center", width: "100%", marginTop: "2rem" }}>
          No recent posts to show yet.
        </p>
      )}
    </Fragment>
  );
};

FollowingPosts.propTypes = {
  token: PropTypes.string,
  showModal: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
};

export default FollowingPosts;
